import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AddIssueOrTaskButton,
  HeaderLayout,
  MeetingHeader,
  MeetingNotesDrawerContent,
  SignalRGroup,
  TextViewer,
  useDrawer,
} from "../../components";
import { Links, MeetingType } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import { queryKeys, useEndMeeting, useGetMeetingParticipants, useGetMeetingDetail, useJoinMeeting } from "../../http";
import { MeetingAgendaStepContent } from "./components";

const StickySideMenuCard = styled(Card)`
  position: sticky;
  top: 176px;
  overflow: visible;
  z-index: 2;
  min-width: 300px;
  min-height: 0;
  height: fit-content;
`;

interface UrlParams {
  meetingGuid: string;
}

const MeetingPage = () => {
  const { meetingGuid } = useParams<keyof UrlParams>() as UrlParams;
  const sessionUser = useSession();
  const { mutate: joinMeeting, isLoading: isJoiningMeeting } = useJoinMeeting();
  const [shouldFetchMeetingData, setShouldFetchMeetingData] = useState(false);
  const [meetingIsClosing, setMeetingIsClosing] = useState(false);
  const { data: meeting, isLoading: isLoadingMeeting } = useGetMeetingDetail(meetingGuid, shouldFetchMeetingData);
  const { data: currentParticipants } = useGetMeetingParticipants(meetingGuid, shouldFetchMeetingData);
  const { mutate: endMeeting, isLoading: isMeetingEnding } = useEndMeeting();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const theme = useTheme();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!meeting) {
      return;
    }

    const stepFromUrl = parseInt(searchParams.get("agendaStep") ?? "0", 10);
    const newStepIndex =
      !isNaN(stepFromUrl) && stepFromUrl >= 0 && stepFromUrl < meeting.agendaItems.length ? stepFromUrl : 0;

    if (newStepIndex !== activeStepIndex) {
      setActiveStepIndex(newStepIndex);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, meeting]);

  useEffect(() => {
    if (meetingIsClosing || currentParticipants?.participants.find((p) => p.userId === sessionUser.userId)) {
      return;
    }

    const timeout = setTimeout(
      () =>
        joinMeeting(
          { meetingGuid: meetingGuid },
          {
            onSuccess: () => {
              setShouldFetchMeetingData(true);
              signalR.sendRefetchMeetingListItem(meetingGuid);
              signalR.sendRefetchMeetingParticipants(meetingGuid);
            },
          }
        ),
      100
    );
    return () => clearTimeout(timeout);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingGuid, currentParticipants]);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Meeting(meetingGuid));
    return () => signalR.leaveGroup(SignalRGroup.Meeting(meetingGuid));
  }, [meetingGuid, signalR]);

  useEffect(() => {
    const handleShouldRefetchParticipants = (meetingGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingParticipants(meetingGuid));
    };

    signalR.onShouldRefetchMeetingParticipants(handleShouldRefetchParticipants);
    return () => signalR.offShouldRefetchMeetingParticipants(handleShouldRefetchParticipants);
  }, [queryClient, signalR]);

  useEffect(() => {
    const handleShouldEndMeeting = () => {
      meeting?.type === MeetingType.Team
        ? navigate(Links.TeamMeetings(meeting.team.slug))
        : navigate(Links.UserMeetings(sessionUser.userId));
    };

    signalR.onShouldEndMeeting(handleShouldEndMeeting);
    return () => signalR.offShouldEndMeeting(handleShouldEndMeeting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.type, navigate, sessionUser.userId, signalR]);

  function handleActiveAgendaStepChanged(newAgendaStepIndex: number) {
    if (meeting && newAgendaStepIndex >= 0 && newAgendaStepIndex < meeting.agendaItems.length) {
      // Update the URL. The useEffect above will handle setting the state.
      setSearchParams({ agendaStep: newAgendaStepIndex.toString() }, { replace: true });
    }
  }

  async function handleCloseMeetingForAll() {
    if (!meeting || !meeting.currentInstance) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Close Meeting",
      content: "Are you sure you want to close the meeting for all participants?",
      confirmationText: "OK",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    setMeetingIsClosing(true);

    endMeeting(
      { meetingGuid: meetingGuid },
      {
        onSuccess: () => {
          signalR.sendRefetchMeetingListItem(meetingGuid);
          signalR.sendEndMeeting(meetingGuid);
          meeting?.type === MeetingType.Team
            ? navigate(Links.TeamMeetings(meeting.team.slug))
            : navigate(Links.UserMeetings(sessionUser.userId));
        },
      }
    );
  }

  const isLoading = isJoiningMeeting || isLoadingMeeting;

  return (
    <HeaderLayout
      title={
        isLoading || meeting == null || meeting.currentInstance == null ? (
          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={40} width={400} />
            <Skeleton variant="rectangular" height={24} width={500} />
          </Stack>
        ) : (
          <MeetingHeader
            meeting={meeting}
            meetingInstance={meeting.currentInstance}
            participants={currentParticipants?.participants}
            isMeetingEnding={isMeetingEnding}
            externalCallUrl={meeting.externalCallUrl}
            onCloseMeetingClicked={handleCloseMeetingForAll}
          />
        )
      }
      actions={
        !isLoading && (
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
            <AddIssueOrTaskButton
              variant="contained"
              defaultSelectedTeamSlug={meeting?.type === MeetingType.Team ? meeting.team.slug : undefined}
            />
          </Stack>
        )
      }
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1}>
          {isLoading && !meeting && (
            <>
              <Card sx={{ flex: 1 }}>
                <CardHeader title={<Skeleton height={28} width={200} />} />
                <Divider />
                <Stack sx={{ p: 1 }}>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </Stack>
              </Card>
              <Box sx={{ flex: 2 }}></Box>
            </>
          )}

          {!isLoading && meeting && meeting.currentInstance && (
            <>
              <StickySideMenuCard sx={{ flex: 1 }}>
                <CardHeader
                  title={
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
                      <Typography variant="h6">Agenda</Typography>
                      <MeetingNotesButton meetingGuid={meetingGuid} meetingType={meeting.type} />
                    </Stack>
                  }
                  sx={{ pr: 0.5 }}
                />
                <Divider sx={{ borderColor: theme.palette.grey[200] }} />
                <Stepper orientation="vertical" activeStep={activeStepIndex} sx={{ p: 1 }}>
                  {meeting.agendaItems
                    .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                    .map((item, index) => (
                      <Step key={item.name}>
                        <StepLabel>
                          <Stack direction="row" spacing={0.5}>
                            <Typography
                              variant="body2"
                              onClick={() => handleActiveAgendaStepChanged(index)}
                              sx={{
                                color: index === activeStepIndex ? theme.palette.primary.main : undefined,
                                cursor: "pointer",
                              }}
                            >
                              {item.name}
                            </Typography>
                            {item.allocatedTimeInMinutes > 0 && (
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >{`${item.allocatedTimeInMinutes} min`}</Typography>
                            )}
                          </Stack>
                        </StepLabel>
                        <StepContent>
                          <Stack spacing={0.5}>
                            <TextViewer value={`<p>${item.description}</p>`} />
                            <Stack direction="row" spacing={0.5}>
                              {index < meeting.agendaItems.length - 1 && (
                                <Button
                                  variant="contained"
                                  onClick={() => handleActiveAgendaStepChanged(activeStepIndex + 1)}
                                >
                                  Next Step
                                </Button>
                              )}

                              {index > 0 && (
                                <Button
                                  variant="outlined"
                                  onClick={() => handleActiveAgendaStepChanged(activeStepIndex - 1)}
                                >
                                  Back
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </StepContent>
                      </Step>
                    ))}
                </Stepper>
              </StickySideMenuCard>

              <Box sx={{ flex: 3, minWidth: 0 }}>
                <MeetingAgendaStepContent agendaStep={meeting.agendaItems[activeStepIndex]} meeting={meeting} />
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    </HeaderLayout>
  );
};

const MeetingNotesButton = (props: { meetingGuid: string; meetingType: MeetingType }) => {
  const sessionUser = useSession();
  const { showDrawer, closeDrawer, isOpen: drawerIsOpen } = useDrawer();

  function handleToggleMeetingNotesDrawer() {
    if (drawerIsOpen) {
      closeDrawer();
    } else {
      showDrawer({
        title:
          props.meetingType === MeetingType.Team ? (
            <Typography variant="h6">Meeting Notes</Typography>
          ) : (
            <Stack>
              <Typography variant="h6">My Meeting Notes</Typography>
              <Typography variant="caption">Meeting notes are accessible only to {sessionUser.displayName}.</Typography>
            </Stack>
          ),
        content: <MeetingNotesDrawerContent meetingGuid={props.meetingGuid} />,
      });
    }
  }

  return (
    <Button variant="tertiary" onClick={handleToggleMeetingNotesDrawer}>
      {drawerIsOpen ? "Hide Meeting Notes" : "Show Meeting Notes"}
    </Button>
  );
};

export { MeetingPage };
